import { faPhone, faTimes, faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Alert, Box, Button, Slide, styled, Typography } from '@mui/material';
import { differenceInYears } from 'date-fns';
import { useEffect, useState } from 'react';

import { usePatientAutocompleteLazyQuery } from '../graphql/patient.generated';
import { useHackathonToggle } from './HackathonToggles';

const HACKATHON_AVATARS = ['homer1.png', 'homer2.png', 'ashlee.png', 'homie.png', 'homey.png', 'dude.png'];

function getAvatarUrl(id?: string): string {
  const index = (Number(id) ?? 0) % HACKATHON_AVATARS.length;
  return `/hackathon-avatars/${HACKATHON_AVATARS[index]}`;
}

const dobFormatter = new Intl.DateTimeFormat('en-US');
const chartUrl = process.env.REACT_APP_CLINICAL_UX_URL;

const BannerRoot = styled(Box)(({ theme }) => ({
  '@keyframes ring': {
    '0%': { transform: 'rotate(-8deg)' },
    '100%': { transform: 'rotate(8deg)' },
  },
  position: 'fixed',
  top: 48,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: theme.zIndex.tooltip + 2,
  width: '600px',
  maxWidth: '90vw',
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  alignItems: 'center',
  boxShadow: theme.shadows[8],
  borderRadius: `${Number(theme.shape.borderRadius) * 2}px`,
  '& .MuiAlert-message': {
    width: '100%',
  },
}));

export function useCallerIdBanner() {
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const { detail } = e as CustomEvent;
      setPhoneNumber(detail?.phoneNumber || null);
    };
    window.addEventListener('hackathon-incoming-call', handler);
    return () => window.removeEventListener('hackathon-incoming-call', handler);
  }, []);

  return { phoneNumber, dismiss: () => setPhoneNumber(null) };
}

function CallerIdBanner() {
  const { phoneNumber, dismiss } = useCallerIdBanner();
  const [search, { data }] = usePatientAutocompleteLazyQuery();
  const showProfilePhoto = useHackathonToggle('showProfilePhoto');
  const showPcp = useHackathonToggle('showPcp');

  useEffect(() => {
    if (phoneNumber) {
      search({ variables: { textQuery: phoneNumber } });
    }
  }, [phoneNumber, search]);

  if (!phoneNumber) return null;

  const match = data?.patientSearch?.[0];
  const hasMultiple = (data?.patientSearch?.length || 0) > 1;

  const handleOpenChart = () => {
    if (match) {
      window.open(
        `${chartUrl}/#/patients/${match.id}/chart/summaries/new`,
        '_blank',
        'noopener noreferrer',
      );
    }
    dismiss();
  };

  const formattedAge = match?.dob
    ? `${differenceInYears(new Date(), new Date(match.dob))} yo`
    : '';
  const formattedDob = match?.dob
    ? dobFormatter.format(new Date(`${match.dob}T00:00:00.000`))
    : '';

  return (
    <BannerRoot>
      <Slide in direction="down">
        <StyledAlert
          severity="info"
          icon={<FontAwesomeIcon icon={faPhone} style={{ fontSize: 20, animation: 'ring 0.5s ease-in-out infinite alternate' }} />}
          action={
            <Button size="small" color="inherit" onClick={dismiss}>
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          }
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Box>
              <Typography variant="body2" color="text.secondary">
                Incoming call from <strong>{phoneNumber}</strong>
              </Typography>
              {match ? (
                <Box display="flex" alignItems="center" gap={1.5} mt={0.5}>
                  {showProfilePhoto && (
                    <Avatar
                      src={getAvatarUrl(match.id)}
                      sx={{ width: 36, height: 36, flexShrink: 0 }}
                    />
                  )}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {match.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formattedDob} ({formattedAge})
                    </Typography>
                    {(match.serviceAreaName || (showPcp && match.pcpName)) && (
                      <Typography variant="caption" color="text.secondary">
                        {[
                          match.serviceAreaName,
                          showPcp && match.pcpName && `PCP: ${match.pcpName}`,
                        ].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                    {hasMultiple && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        +{(data?.patientSearch?.length || 1) - 1} more matches
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  No patient match found
                </Typography>
              )}
            </Box>
            {match && (
              <Button
                variant="contained"
                size="small"
                onClick={handleOpenChart}
                sx={{ ml: 2, whiteSpace: 'nowrap' }}
              >
                Open Chart <FontAwesomeIcon icon={faExternalLink} style={{ marginLeft: 6 }} />
              </Button>
            )}
          </Box>
        </StyledAlert>
      </Slide>
    </BannerRoot>
  );
}

export default CallerIdBanner;
